/**
 * Online status under the name in the person sidebar (t173u): "Online", "Away", or "Last active 2h ago" with the exact
 * time on hover. The live status comes from the realtime store (RealtimeBridge's 30-second snapshot); last seen comes
 * from GET /workspaces/:ws/presence?user_ids=<id>, refetched whenever the live status changes.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PresenceStatus } from '@tradeoutlet/shared';
import { api } from '@/lib/api';
import { useUi } from '@/lib/store';
import { exactDateTime, lastActiveAgo } from '@/lib/format';
import { HoverBubble } from '@/components/ui/HoverBubble';
import { useRelativeTick } from '@/components/time/RelativeTime';

export function usePersonPresence(ws: string | null | undefined, userId: string | null | undefined) {
  const live = useUi(s => (userId ? s.presence[userId] : undefined));
  const q = useQuery({
    queryKey: ['person-presence', ws, userId],
    queryFn: async () => (await api.presence(ws!, [userId!])).presence.find(e => e.user_id === userId) ?? null,
    enabled: Boolean(ws && userId),
    staleTime: 20_000,
  });
  const { refetch } = q;
  // Going offline (or coming back) changes last seen: pick it up as soon as the store notices.
  useEffect(() => { if (live && userId) void refetch(); }, [live, userId, refetch]);
  const status: PresenceStatus = live ?? q.data?.status ?? 'offline';
  return { status, lastSeenAt: q.data?.last_seen_at ?? null, loaded: q.isFetched };
}

export function PresenceLine({ status, lastSeenAt }: { status: PresenceStatus; lastSeenAt: string | null }) {
  useRelativeTick();
  const base = 'mt-0.5 text-xs';
  if (status === 'online') return <p className={`${base} font-medium text-emerald-700 dark:text-emerald-400`} data-testid="person-presence">Online</p>;
  if (status === 'away') return <p className={`${base} font-medium text-orange-700 dark:text-orange-400`} data-testid="person-presence">Away</p>;
  if (!lastSeenAt) return <p className={`${base} text-slate-500 dark:text-slate-400`} data-testid="person-presence">Offline</p>;
  return (
    <div className={`${base} text-slate-500 dark:text-slate-400`} data-testid="person-presence">
      <HoverBubble bubble={exactDateTime(lastSeenAt)}>
        <time dateTime={lastSeenAt} tabIndex={0} className="rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1">Last active {lastActiveAgo(lastSeenAt)}</time>
      </HoverBubble>
    </div>
  );
}
